import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import useProductName from "brand-customization/useProductName";
import useAssistantHistory from "ai/hooks/useAssistantHistory";
import Spinner from "components/common/Spinner";
import Alert from "components/bootstrap/Alert";
import IconButton from "components/common/IconButton";
import Markdown from "components/common/Markdown";
import useMarkdownConfig from "components/common/useMarkdownConfig";
import { Link } from "components/common";
import usePermissions from "hooks/usePermissions";
import useFeature from "hooks/useFeature";
import AIManagementRoutes from "ai-management/Routes";
import { AGENT_WORKFLOW_FEATURE_FLAG } from "aiagent/Constants";
import useAgentWorkflowsLicensed from "aiagent/hooks/useAgentWorkflowsLicensed";
import {
  SidebarContainer,
  SidebarRow,
  SidebarContentArea,
  SidebarHeader,
  SidebarTitle
} from "components/layout/RightSidebar";
import useAssistantSidebar from "./useAssistantSidebar";
import WidgetSummaryQuestionPrompt from "./WidgetSummaryQuestionPrompt";
import ResponseActions from "./ResponseActions";
import SummarySubject from "./SummarySubject";
import AssistantAttachment from "./AssistantAttachment";
import ChatBubble from "./ChatBubble";
import TimestampSeparator from "./TimestampSeparator";
const SIDEBAR_WIDTH = 400;
const SidebarPanel = styled(SidebarContainer)`
  overflow: hidden;
`;
const HistoryTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
const Question = ({ request }) => {
  var _a;
  return /* @__PURE__ */ React.createElement(QuestionContainer, null, /* @__PURE__ */ React.createElement(ChatBubble, null, /* @__PURE__ */ React.createElement(AssistantAttachment, { request, size: "compact" }), /* @__PURE__ */ React.createElement("span", null, (_a = request.question) != null ? _a : /* @__PURE__ */ React.createElement(React.Fragment, null, "AI summary of ", /* @__PURE__ */ React.createElement(SummarySubject, { request }), "."))));
};
const ResponseContainer = styled.div`
  margin-bottom: 20px;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid transparent;
  animation: ${({ $highlight }) => $highlight ? "highlight-fade 5s" : "none"};

  ul {
    list-style-type: disc;
    padding-left: 1.5rem;

    > li {
      padding-bottom: 4px;
    }
  }

  @keyframes highlight-fade {
    from {
      border-color: ${({ theme }) => theme.colors.variant.success};
      background-color: ${({ theme }) => theme.colors.background.secondaryNav};
    }

    to {
      border-color: transparent;
      background-color: inherit;
    }
  }
`;
const Response = ({ response, highlight = false }) => /* @__PURE__ */ React.createElement(ResponseContainer, { $highlight: highlight, "data-testid": "assistant-response" }, /* @__PURE__ */ React.createElement(Markdown, { augment: true, text: response.text }), /* @__PURE__ */ React.createElement(ResponseActions, { response }));
const AssistantEntry = ({ history, highlight = false }) => /* @__PURE__ */ React.createElement(HistoryTimeline, null, /* @__PURE__ */ React.createElement(TimestampSeparator, { timestamp: history.timestamp }), /* @__PURE__ */ React.createElement(Question, { request: history.request }), /* @__PURE__ */ React.createElement(Response, { response: history.response, highlight }));
const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;
const Subtitle = styled.p(
  ({ theme }) => css`
    margin: 0;
    font-size: ${theme.fonts.size.small};
    color: ${theme.colors.text.secondary};
  `
);
const ModelProviderHint = () => {
  const { isPermitted } = usePermissions();
  const isFeatureEnabled = useFeature(AGENT_WORKFLOW_FEATURE_FLAG);
  const isLicensed = useAgentWorkflowsLicensed();
  if (!isPermitted("llm_provider:edit") || !isFeatureEnabled || !isLicensed) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Subtitle, null, "Responses use the configured ", /* @__PURE__ */ React.createElement(Link, { to: AIManagementRoutes.MODEL_PROVIDERS }, "Model Providers"), ".");
};
const Header = () => {
  const { close } = useAssistantSidebar();
  const productName = useProductName();
  return /* @__PURE__ */ React.createElement(SidebarHeader, null, /* @__PURE__ */ React.createElement(HeaderText, null, /* @__PURE__ */ React.createElement(SidebarTitle, null, productName, " Assistant"), /* @__PURE__ */ React.createElement(ModelProviderHint, null)), /* @__PURE__ */ React.createElement(IconButton, { name: "close", title: "Close sidebar", onClick: close, "aria-label": "Close sidebar" }));
};
const FooterContainer = styled.div(
  ({ theme }) => css`
    padding: 10px 15px;
    font-size: ${theme.fonts.size.small};
    color: ${theme.colors.text.secondary};
  `
);
const Footer = () => /* @__PURE__ */ React.createElement(FooterContainer, null, "AI-generated summaries may occasionally contain inaccurate or misleading information. Always verify critical data independently.");
const toMillis = (timestamp) => new Date(timestamp).getTime();
const latestTimestamp = (entries) => entries.reduce((latest, { timestamp }) => Math.max(latest, toMillis(timestamp)), -Infinity);
const AssistantSidebar = () => {
  const { data, isLoading: isLoadingHistory } = useAssistantHistory();
  const { isLoading: isLoadingMarkdownConfig } = useMarkdownConfig();
  const isLoading = isLoadingHistory || isLoadingMarkdownConfig;
  const { widgetSummaryRequest } = useAssistantSidebar();
  const messagesEndRef = useRef(null);
  const [latestSeen, setLatestSeen] = useState(null);
  const newestTimestamp = isLoading ? null : latestTimestamp(data);
  if (!isLoading && latestSeen === null) {
    setLatestSeen(latestTimestamp(data));
  }
  useEffect(() => {
    var _a;
    if (!isLoading && (messagesEndRef == null ? void 0 : messagesEndRef.current)) {
      (_a = messagesEndRef.current) == null ? void 0 : _a.scrollIntoView({ behavior: "instant", block: "end" });
    }
  }, [isLoading, widgetSummaryRequest, newestTimestamp]);
  return /* @__PURE__ */ React.createElement(SidebarPanel, { $width: SIDEBAR_WIDTH, role: "complementary", "aria-label": "Assistant sidebar" }, /* @__PURE__ */ React.createElement(SidebarRow, { className: "content" }, /* @__PURE__ */ React.createElement(Header, null), /* @__PURE__ */ React.createElement(SidebarContentArea, null, isLoading ? /* @__PURE__ */ React.createElement(Spinner, null) : data.map((history) => /* @__PURE__ */ React.createElement(
    AssistantEntry,
    {
      key: String(history.timestamp),
      history,
      highlight: latestSeen !== null && toMillis(history.timestamp) > latestSeen
    }
  )), !isLoading && data.length === 0 && !widgetSummaryRequest && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "Your history is currently empty."), widgetSummaryRequest && /* @__PURE__ */ React.createElement(WidgetSummaryQuestionPrompt, { request: widgetSummaryRequest }), /* @__PURE__ */ React.createElement("div", { ref: messagesEndRef })), /* @__PURE__ */ React.createElement(Footer, null)));
};
const AISidebarWrapper = () => {
  const { isOpen } = useAssistantSidebar();
  return isOpen ? /* @__PURE__ */ React.createElement(AssistantSidebar, null) : null;
};
export default AISidebarWrapper;
